import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n, { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Label } from "@libs/zapi-ui/ds/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { Separator } from "@libs/zapi-ui/ds/components/separator";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { languageOptions } from "@/zapi-frontend/widgets/CreateProjectForm/const/const";
import { useUserPreferences, TUserLanguage } from "../services/useUserPreferences";
import { useUpdateUserPreferences } from "../services/useUpdateUserPreferences";

export function PreferencesSection() {
  const { t } = useTranslation(["settings", "common"]);
  const { refetch: refetchMe } = useMeStore();
  const queryClient = useQueryClient();
  const { data: preferences, isLoading } = useUserPreferences();
  const [uiLanguage, setUiLanguage] = useState<TUserLanguage>("english");

  useEffect(() => {
    if (preferences) setUiLanguage(preferences.uiLanguage);
  }, [preferences]);

  const { mutate: updatePreferences, isPending } = useUpdateUserPreferences({
    onSuccess: async (updated) => {
      queryClient.setQueryData(["userPreferences"], updated);
      await refetchMe?.();
      // The interface switches in an effect that has not run yet, so `t` would still resolve
      // against the outgoing language. Announce the save in the language just chosen.
      const savedT = i18n.getFixedT(projectLanguageToLng(updated.uiLanguage), "settings");
      toast.success(savedT("preferences.saved"));
    },
    onError: (error) => toast.error(error.response?.data?.message ?? t("preferences.saveFailed"))
  });

  const isChanged = Boolean(preferences) && uiLanguage !== preferences?.uiLanguage;

  const languageLabels = Object.fromEntries(
    languageOptions.map((option) => {
      const name = t(`languageName.${option.value}`);
      return [option.value, name === option.endonym ? name : `${name} (${option.endonym})`];
    })
  );

  if (isLoading) return <Spinner aria-label={t("common:loading")} className="size-5 text-muted-foreground" />;

  return (
    <div className="flex flex-col gap-4 max-w-[640px]">
      <p className="typo-h6 text-innoveraZinc-900">{t("preferences.heading")}</p>
      <Separator />

      <div className="flex flex-col gap-2">
        <Label htmlFor="ui-language">{t("preferences.uiLanguage")}</Label>
        <Select
          items={languageLabels}
          value={uiLanguage}
          onValueChange={(value) => value !== null && setUiLanguage(value as TUserLanguage)}
          disabled={isPending}
        >
          <SelectTrigger id="ui-language" className="w-[250px]" data-testid="preferences-ui-language-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {languageOptions.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                data-testid={`preferences-ui-language-${option.value}-item`}
              >
                {languageLabels[option.value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="typo-caption text-innoveraZinc-500">{t("preferences.uiLanguageHint")}</p>
      </div>

      <div>
        <Button
          type="button"
          data-testid="preferences-save-button"
          onClick={() => updatePreferences({ uiLanguage })}
          disabled={!isChanged || isPending}
        >
          {isPending ? (
            <>
              <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
              {t("common.saving")}
            </>
          ) : (
            t("common.save")
          )}
        </Button>
      </div>
    </div>
  );
}
